/**
 * Explore's table: "Showing N of M Rows", a search box, sortable columns (the
 * view's dimension plus every enabled metric), an in-widget dimension switch
 * where the view has one (Location: Country / Region / City), and pages of
 * 50 rows. Search and paging run on the server, so they reach every row.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { formatValue } from '../../reports/format.ts';
import type { CatalogueMetric, WidgetResult } from '../../reports/reports-api.ts';
import { countryFlag } from './country-flag.ts';
import { TABLE_PAGE } from './use-explore-data.ts';

const SEARCH_DELAY_MS = 300;

export interface TableState {
  dimension: string;
  search: string;
  sort: { metric: string; direction: 'asc' | 'desc' };
  page: number;
}

export function ExploreTable(props: {
  dimensions: { key: string; label: string }[];
  metrics: CatalogueMetric[];
  result: WidgetResult | null;
  state: TableState;
  onChange: (state: TableState) => void;
}): ReactNode {
  const { state, onChange, result } = props;
  const [typed, setTyped] = useState(state.search);
  useEffect(() => {
    if (typed === state.search) return undefined;
    const timer = window.setTimeout(
      () => onChange({ ...state, search: typed, page: 0 }),
      SEARCH_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [typed]);

  const rows = result?.rows ?? [];
  const total = result?.total_rows ?? 0;
  const pages = Math.max(1, Math.ceil(total / TABLE_PAGE));
  const dimension = props.dimensions.find((item) => item.key === state.dimension);
  const sortBy = (metric: string) =>
    onChange({
      ...state,
      page: 0,
      sort: {
        metric,
        direction: state.sort.metric === metric && state.sort.direction === 'desc' ? 'asc' : 'desc',
      },
    });

  return (
    <section className="ex-panel ex-table" aria-label="Table">
      <div className="ex-table-head">
        <span className="ex-table-count">
          Showing {rows.length} of {total} Rows
        </span>
        {props.dimensions.length > 1 && (
          <div className="ex-switch" role="group" aria-label="Breakdown">
            {props.dimensions.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-pressed={item.key === state.dimension}
                onClick={() => onChange({ ...state, dimension: item.key, search: '', page: 0 })}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        <input
          type="search"
          className="ex-search"
          placeholder="Search"
          aria-label={`Search ${dimension?.label ?? 'rows'}`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>
      <div className="ex-table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">{dimension?.label ?? 'Value'}</th>
              {props.metrics.map((metric) => {
                const active = state.sort.metric === metric.key;
                return (
                  <th
                    key={metric.key}
                    scope="col"
                    className="num"
                    aria-sort={
                      active
                        ? state.sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button type="button" onClick={() => sortBy(metric.key)}>
                      {metric.label}
                      {active && (
                        <span aria-hidden="true">
                          {state.sort.direction === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">
                  {state.dimension === 'country' && (
                    <span className="ex-flag" aria-hidden="true">
                      {countryFlag(row.label)}
                    </span>
                  )}
                  {row.label || '—'}
                </th>
                {props.metrics.map((metric) => (
                  <td key={metric.key} className="num">
                    {formatValue(row.values[metric.key] ?? null, metric.format)}
                  </td>
                ))}
              </tr>
            ))}
            {result && rows.length === 0 && (
              <tr>
                <td colSpan={props.metrics.length + 1} className="ex-none">
                  No data available in the table
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <nav className="ex-pager" aria-label="Table pages">
          <button
            type="button"
            disabled={state.page === 0}
            onClick={() => onChange({ ...state, page: state.page - 1 })}
          >
            Previous
          </button>
          <span>
            Page {state.page + 1} of {pages}
          </span>
          <button
            type="button"
            disabled={state.page + 1 >= pages}
            onClick={() => onChange({ ...state, page: state.page + 1 })}
          >
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
